import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme, 
  alpha,
  Button,
  Chip,
  Alert
} from '@mui/material';
import { fetchRestaurantInfo } from './api';
import config from './config';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Icons
import ShieldIcon from '@mui/icons-material/Shield';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StorageIcon from '@mui/icons-material/Storage';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HelpIcon from '@mui/icons-material/Help';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

function PoliciesTerms() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchRestaurantInfo().then(setInfo).catch(console.error);
    
    // Fetch slider images for background
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const image = data.find(img => img.IsActive);
          if (image) {
            const imgPath = image.ImagePath.startsWith('http') 
              ? image.ImagePath 
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const policySections = [
    {
      id: 'terms',
      icon: <DescriptionIcon />,
      title: t('policies.termsOfService', 'Terms of Service'),
      color: '#2196F3',
      content: [
        t('policies.terms1'),
        t('policies.terms2'),
        t('policies.terms3'),
        t('policies.terms4'),
        t('policies.terms5'),
        t('policies.terms6'),
      ]
    },
    {
      id: 'privacy',
      icon: <SecurityIcon />,
      title: t('policies.privacyPolicy', 'Privacy Policy'),
      color: '#4CAF50',
      content: [
        t('policies.privacy1'),
        t('policies.privacy2'),
        t('policies.privacy3'),
        t('policies.privacy4'),
        t('policies.privacy5'),
        t('policies.privacy6'),
      ]
    },
    {
      id: 'multiCurrency',
      icon: <PublicIcon />,
      title: t('policies.multiCurrency', 'Multi-Currency Support'),
      color: '#3F51B5',
      content: [
        t('policies.multiCurrency1'),
        t('policies.multiCurrency2'),
        t('policies.multiCurrency3'),
        t('policies.multiCurrency4'),
        t('policies.multiCurrency5'),
        t('policies.multiCurrency6'),
      ]
    },
    {
      id: 'walletPayment',
      icon: <AccountBalanceWalletIcon />,
      title: t('policies.walletPayment', 'E-Wallet Payment'),
      color: '#00BCD4',
      content: [
        t('policies.walletPayment1'),
        t('policies.walletPayment2'),
        t('policies.walletPayment3'),
        t('policies.walletPayment4'),
        t('policies.walletPayment5'),
        t('policies.walletPayment6'),
        t('policies.walletPayment7'),
      ]
    },
    {
      id: 'cancellation',
      icon: <CancelIcon />,
      title: t('policies.cancellationPolicy', 'Order Cancellation Policy'),
      color: '#FF9800',
      content: [
        t('policies.cancellation1'),
        t('policies.cancellation2'),
        t('policies.cancellation3'),
        t('policies.cancellation4'),
        t('policies.cancellation5'),
        t('policies.cancellation6'),
      ]
    },
    {
      id: 'refund',
      icon: <BlockIcon />,
      title: t('policies.refundPolicy', 'No Refund Policy'),
      color: '#F44336',
      content: [
        t('policies.refund1'),
        t('policies.refund2'),
        t('policies.refund3'),
        t('policies.refund4'),
        t('policies.refund5'),
        t('policies.refund6'),
      ]
    },
    {
      id: 'payment',
      icon: <PaymentIcon />,
      title: t('policies.paymentPolicy', 'Payment Policy'),
      color: '#E91E63',
      content: [
        t('policies.payment1'),
        t('policies.payment2'),
        t('policies.payment3'),
        t('policies.payment4'),
        t('policies.payment5'),
        t('policies.payment6'),
      ]
    },
    {
      id: 'delivery',
      icon: <LocalShippingIcon />,
      title: t('policies.deliveryPolicy', 'Delivery Policy'),
      color: '#9C27B0',
      content: [
        t('policies.delivery1'),
        t('policies.delivery2'),
        t('policies.delivery3'),
        t('policies.delivery4'),
        t('policies.delivery5'),
        t('policies.delivery6'),
        t('policies.delivery7'),
      ]
    },
    {
      id: 'usage',
      icon: <PhoneAndroidIcon />,
      title: t('policies.usagePolicy', 'App Usage Policy'),
      color: '#00BCD4',
      content: [
        t('policies.usage1'),
        t('policies.usage2'),
        t('policies.usage3'),
        t('policies.usage4'),
        t('policies.usage5'),
        t('policies.usage6'),
      ]
    },
    {
      id: 'data',
      icon: <StorageIcon />,
      title: t('policies.dataProtection', 'Data Protection'),
      color: '#FF5722',
      content: [
        t('policies.data1'),
        t('policies.data2'),
        t('policies.data3'),
        t('policies.data4'),
        t('policies.data5'),
        t('policies.data6'),
      ]
    },
    {
      id: 'responsibility',
      icon: <VerifiedUserIcon />,
      title: t('policies.customerResponsibility', 'Customer Responsibility'),
      color: '#795548',
      content: [
        t('policies.responsibility1'),
        t('policies.responsibility2'),
        t('policies.responsibility3'),
        t('policies.responsibility4'),
        t('policies.responsibility5'),
        t('policies.responsibility6'),
      ]
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative', 
        height: 350, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white',
        mb: 4,
        overflow: 'hidden'
      }}>
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: bgImage 
              ? `url(${bgImage})` 
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(3px)',
            transform: 'scale(1.1)'
        }} />
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to bottom, ${alpha('#000', 0.4)} 0%, ${alpha('#000', 0.8)} 100%)`,
            zIndex: 1
        }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Box sx={{ 
              bgcolor: alpha('#fff', 0.2), 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <ShieldIcon sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h2" fontWeight="900" sx={{ mb: 2, textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                {t('policies.title', 'Policies & Terms')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
                {t('policies.subtitle', 'Please read our policies before placing an order')}
            </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Important Notice Alert */}
        <Alert 
          severity="warning" 
          icon={<WarningAmberIcon fontSize="large" />}
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'warning.main',
            boxShadow: '0 4px 20px rgba(237, 108, 2, 0.2)',
            '& .MuiAlert-icon': {
              fontSize: 40
            }
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t('policies.importantNotice', 'Important Notice')}
          </Typography>
          <Box component="ul" sx={{ mt: 2, pl: 2 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>⚠️ {t('policies.notice1', 'Orders can only be cancelled if products are not yet prepared')}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>🚫 {t('policies.notice2', 'We do not offer refunds under any circumstances')}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>💵 {t('policies.notice3', 'Payment is cash on delivery - hand to hand upon receiving')}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>💳 {t('policies.notice4', 'We now support e-wallet payment with transaction proof verification')}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>🌍 {t('policies.notice5', 'Multiple currencies are supported for your convenience')}</strong>
              </Typography>
            </li>
          </Box>
        </Alert>

        {/* Introduction Card */}
        <Card sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <Typography variant="h5" fontWeight="800" gutterBottom>
            {t('policies.welcome', 'Important Information')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
            {t('policies.introText', 'Please read these policies carefully before placing an order. By using our app and services, you agree to these terms and conditions. Click on each section below to read more details.')}
          </Typography>
          <Chip 
            icon={<AccessTimeIcon />}
            label={`${t('policies.lastUpdated', 'Last Updated')}: ${new Date().toLocaleDateString()}`}
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 600
            }}
          />
        </Card>

        {/* Policy Accordions */}
        <Box sx={{ mb: 4 }}>
          {policySections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expanded === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: expanded === section.id 
                  ? `0 8px 24px ${alpha(section.color, 0.15)}` 
                  : '0 2px 8px rgba(0,0,0,0.05)',
                '&:before': { display: 'none' },
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                borderLeft: `4px solid ${section.color}`,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  py: 2,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 2
                  }
                }}
              >
                <Box sx={{ 
                  bgcolor: alpha(section.color, 0.1), 
                  color: section.color,
                  width: 50, 
                  height: 50, 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  {section.icon}
                </Box>
                <Typography variant="h6" fontWeight="700">
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 2 } }}>
                  {section.content.map((item, index) => (
                    <li key={index}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {item}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Contact Support Card */}
        <Card sx={{ 
          p: 5, 
          textAlign: 'center', 
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
        }}>
          <HelpIcon sx={{ fontSize: 70, color: 'primary.main', mb: 2, opacity: 0.9 }} />
          <Typography variant="h4" fontWeight="800" gutterBottom>
            {t('policies.questionsTitle', 'Have Questions?')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto', lineHeight: 1.8 }}>
            {t('policies.questionsText', 'If you have any questions about these policies or need clarification, please don\'t hesitate to contact our support team. We\'re here to help!')}
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<ChatIcon />}
            onClick={() => navigate('/Support')}
            sx={{ 
              borderRadius: 30, 
              px: 5,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('common.contactSupport', 'Contact Support')}
          </Button>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center', pb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} {info?.Name || 'Store'}. {t('footer.allRightsReserved', 'All rights reserved.')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            v1.0.0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default PoliciesTerms;
